import React, { useEffect, useState } from "react";
import _ from "underscore";

import RestrictedComponent, {
    hasPermission,
} from "../../../../lib/components/form/RestrictedComponent";
import schema from "../../../../routes/permissions/schema";

import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListSubheader from "@mui/material/ListSubheader";
import { useTheme } from "@mui/material/styles";
import { VariableSizeList } from "react-window";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";

const filter = createFilterOptions();

function getAssignedKeywords(props) {
    var keywords = props.event.keywords;
    var byName = _.indexBy(props.data, "keyword");
    return createOptions(keywords.map((k) => byName[k]));
}

function createOptions(keywords) {
    return keywords.map(function (keyword) {
        return {
            label: keyword.keyword,
            value: keyword.keywordID,
        };
    });
}

const formatKeywordsWithKeywordID = (keywordStringArr, allKeywords) => {
    const byName = _.indexBy(allKeywords, "keyword");

    return keywordStringArr.map((keyword) => {
        return {
            keyword: keyword,
            keywordID: byName[keyword] ? byName[keyword].keywordID : null,
        };
    });
};

const EventKeywords = (props) => {
    const { onSave, event, isResolving } = props;
    const [assignedKeywordOptions, setAssignedKeywordOptions] = useState([]);

    useEffect(() => {
        setAssignedKeywordOptions(getAssignedKeywords(props));
    }, [props.event.keywords]);

    const save = () => {
        onSave({
            eventID: event.eventID,
            keywords: formatKeywordsWithKeywordID(
                assignedKeywordOptions.map((option) =>
                    typeof option.label === "string"
                        ? option.label
                        : option.label.inputValue
                ),
                props.data
            ),
        });
    };

    const onChange = (ev, newEventKeywords) => {
        if (ev.type === "keydown" && ev.key === "Backspace") {
            return;
        }

        const byName = _.indexBy(props.data, "keyword");

        setAssignedKeywordOptions(
            newEventKeywords.map((keyword) => {
                return {
                    label:
                        typeof keyword === "string"
                            ? keyword
                            : keyword.inputValue,
                    value: byName[keyword] ? byName[keyword].keywordID : null,
                };
            })
        );
    };

    const reset = (ev) => {
        ev.preventDefault();
        setAssignedKeywordOptions(getAssignedKeywords(props));
    };

    const updateButtonDisabled =
        isResolving ||
        !hasPermission(schema.EVENT_TAB_KEYWORDS.UPDATE) ||
        _.isEqual(assignedKeywordOptions, getAssignedKeywords(props));

    return (
        <div className="tab-keywords">
            <div className="inv-form">
                <div className="inv-label">Add or remove keywords</div>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        mt: "-4px",
                        pr: "1rem",
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <RestrictedComponent
                            permissionCheck={
                                schema.MANAGE_CLIENT_KEYWORDS.CREATE
                            }
                        >
                            <Virtualize
                                key={"keywords multiselect"}
                                allKeywords={props.data}
                                assignedKeywordOptions={assignedKeywordOptions}
                                readOnly={isResolving}
                                onChange={onChange}
                            />
                        </RestrictedComponent>
                    </Box>

                    <button
                        className="btn btn-default"
                        disabled={updateButtonDisabled}
                        onClick={reset}
                    >
                        <i className="fa fa-undo" /> Reset
                    </button>
                    <button
                        className="btn btn-success"
                        disabled={updateButtonDisabled}
                        onClick={save}
                    >
                        <i className="fa fa-save" /> Save
                    </button>
                </Box>
            </div>
        </div>
    );
};

export default EventKeywords;

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        fontSize: "14px",
        top: style.top + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty("group")) {
        return (
            <ListSubheader
                key={dataSet.key}
                component="div"
                style={inlineStyle}
            >
                {dataSet.group}
            </ListSubheader>
        );
    }

    return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
            {dataSet[1]}
        </Typography>
    );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
    props,
    ref
) {
    const { children, ...other } = props;
    const itemData = [];
    children.forEach((item) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
        noSsr: true,
    });

    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child) => {
        if (child.hasOwnProperty("group")) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref}>
            <OuterElementContext.Provider value={other}>
                <VariableSizeList
                    itemData={itemData}
                    height={getHeight() + 2 * LISTBOX_PADDING}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType="ul"
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={3}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
        </div>
    );
});

const AutocompleteInput = (params) => {
    return (
        <TextField
            variant="outlined"
            key={"keywords multiselect"}
            InputLabelProps={{ shrink: false }}
            {...params}
            label="Keywords"
            autoFocus
            margin="dense"
            sx={{
                backgroundColor: "#fff",
                "& .MuiInputLabel-formControl": {
                    display: "none",
                },
                "& legend": { display: "none" },
                "& fieldset": { top: 0 },

                // font size
                "& .MuiInputBase-input": {
                    fontSize: "14px",
                },

                // padding
                "& .MuiOutlinedInput-root": {
                    paddingY: "2px",
                    paddingLeft: "8px",
                },
            }}
        />
    );
};

function Virtualize({
    allKeywords,
    assignedKeywordOptions,
    onChange,
    readOnly,
}) {
    return (
        <Autocomplete
            multiple
            disableClearable
            selectOnFocus
            handleHomeEndKeys
            freeSolo
            disableListWrap
            disablePortal
            readOnly={
                readOnly || !hasPermission("manage_client_keywords.create")
            }
            ChipProps={{ sx: { fontSize: "12px" } }}
            onChange={onChange}
            value={assignedKeywordOptions.map((option) => option.label) || []}
            options={allKeywords.map((keyword) => keyword.keyword)}
            sx={{ width: "100%", fontSize: "16px" }}
            ListboxComponent={ListboxComponent}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);

                const { inputValue } = params;
                // Suggest the creation of a new value
                const isExisting = options.some(
                    (option) => option === inputValue
                );

                if (
                    inputValue !== "" &&
                    inputValue.trim() !== "" &&
                    !isExisting
                ) {
                    filtered.push({
                        inputValue,
                        title: `Add "${inputValue}"`,
                    });
                }

                return filtered;
            }}
            renderOption={(props, option) => {
                if (typeof option === "string") {
                    return [props, option];
                }

                return [props, option.title];
            }}
            getOptionLabel={(option) => {
                // Value selected with enter, right from the input
                if (typeof option === "string") {
                    return option;
                }
                // Add "xxx" option created dynamically
                if (option && option.inputValue) {
                    return option.inputValue;
                }
                // Regular option
                return option.title;
            }}
            renderInput={(params) => <AutocompleteInput {...params} />}
        />
    );
}
